import { Stack } from "@mui/material";
import { Control, FieldErrors, FieldValues, Path, RegisterOptions, useController, useWatch } from "react-hook-form";
import { v4 } from "uuid";

import { ChoiceFormData } from "../types";
import { ChoiceRowV4 } from "./ChoiceRowV4";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { Add } from "@/icons/outlined";

interface ChoicesSectionProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  rules?: RegisterOptions[];
  isRequired: boolean;
  errors?: FieldErrors;
  minimumChoicesRequired: number;
  isMaximumActive?: boolean;
  isMaximumEnabled?: boolean;
}

export function ChoicesSection<T extends FieldValues>({
  control,
  name,
  rules,
  isRequired,
  errors,
  minimumChoicesRequired,
  isMaximumActive = false,
  isMaximumEnabled = false,
}: ChoicesSectionProps<T>) {
  const { t } = useTranslate();

  const { field } = useController({
    control,
    name,
    rules: {
      ...rules,
      required: {
        value: isRequired,
        message: "common.isRequired",
      },
    },
  });

  const { onChange } = field;
  const watchedChoices = useWatch({ control, name }) as ChoiceFormData[];

  const choices = watchedChoices || [];
  const showMaximumField = isMaximumActive && isMaximumEnabled;

  const choiceIdsForCounts = choices.filter((c) => c.id).map((c) => c.id!);
  const { data: answerCounts } = trpc.getChoiceAnswerCounts.useQuery(
    { choiceIds: choiceIdsForCounts },
    { enabled: choiceIdsForCounts.length > 0 && showMaximumField }
  );

  const addChoice = () => {
    onChange([...watchedChoices, { _key: v4(), body: "", sortIndex: watchedChoices.length, maximum: null }]);
  };

  const deleteChoice = (index: number) => () => {
    onChange(watchedChoices.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={2} sx={{ pl: 3 }}>
      <Stack spacing={1}>
        {choices.map((choice, index) => (
          <ChoiceRowV4
            key={choice._key}
            choice={choice}
            index={index}
            control={control}
            name={name}
            field={field}
            errors={errors}
            isRequired={index < minimumChoicesRequired}
            showMaximumField={showMaximumField}
            showDelete={choices.length > minimumChoicesRequired}
            answerCounts={answerCounts}
            onDelete={deleteChoice(index)}
          />
        ))}
      </Stack>

      <DashboardButton
        variant="tonal"
        vibe="brand"
        size="small"
        startIcon={<Add />}
        onClick={addChoice}
        sx={{ alignSelf: "flex-start" }}
        data-test="v3-add-choice-button"
      >
        {t("dashboard", "donationForm.add")}
      </DashboardButton>
    </Stack>
  );
}
